import { Checkbox, Grid, List, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { AccessRightContext } from "../../user/AccessRightProvider";
import CardsList from "../CardsList";
import WhiteCard from "../WhiteCard";
import { CustomerContract } from "../customerAPI";
import { selectAllCustomerContracts } from "../customerContractsSlice";
import {
  fetchOrderTemplates,
  selectAllOrderTemplates,
  unsetOrderTemplates,
} from "../orderTemplatesSlice";
import { ModalState } from "../types/types";
import { sortListItemsByActiveStatus } from "../utils";
import OrderTemplateForm from "./OrderTemplateForm";

const OrderTemplateList = () => {
  const [showArchived, setShowArchived] = useState(false);

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });
  const [searchParams] = useSearchParams();
  const { canEditOrderTemplate } = useContext(AccessRightContext);

  const eezyCompanyId = searchParams.get("ecom");

  const canEdit = !!eezyCompanyId && canEditOrderTemplate(eezyCompanyId);

  const orderTemplates = useAppSelector((state) =>
    selectAllOrderTemplates(state),
  );

  const orderTemplatesByActiveStatus = showArchived
    ? orderTemplates.sort(sortListItemsByActiveStatus)
    : orderTemplates
        .filter((orderTemplate) => orderTemplate.active === !showArchived)
        .sort((a, b) => a.name.localeCompare(b.name));

  const { status } = useAppSelector((state) => state.orderTemplates);
  const isLoading = status === "pending";

  const localized = useAppSelector((state) => state.localized);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const contract: CustomerContract = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  ).find((contract) => contract.eezyCompany.id === eezyCompanyId);

  const contractId = contract?.id;

  // TODO: add proper cleanup for customer management
  useEffect(() => {
    if (contractId) {
      dispatch(fetchOrderTemplates({ contractId }));
    } else {
      dispatch(unsetOrderTemplates());
    }
  }, [contractId, dispatch]);

  const onOpen = (orderTemplateId?: string) =>
    setModalState({ show: true, id: orderTemplateId });

  const onClose = () => setModalState({ show: false, id: "" });

  return (
    <Grid container flexDirection="column">
      <OverlaySpinner isLoading={isLoading} />
      <Grid item container alignItems="center" padding="19px">
        <Checkbox
          sx={{ color: "#00000085" }}
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
        />
        <Typography variant="secondary">
          {t("customers:orderTemplate:showArchivedOrderTemplates")}
        </Typography>
      </Grid>
      <CardsList
        buttonLabel={t("customers:orderTemplate:orderTemplate")}
        onClick={(id?: string) => onOpen(id)}
        accessRightChecks={[canEdit]}
      >
        <List disablePadding>
          {orderTemplatesByActiveStatus.map((orderTemplate) => (
            <WhiteCard
              key={orderTemplate.id}
              onClick={() => onOpen(orderTemplate.id)}
              primary={orderTemplate.name}
              secondary={localized.tes[orderTemplate.tesId]}
              active={orderTemplate.active}
            />
          ))}
        </List>
      </CardsList>
      <DialogComponent
        variant="card"
        open={modalState.show}
        onClose={onClose}
        sx={{ minWidth: "963px" }}
      >
        <OrderTemplateForm orderTemplateId={modalState.id} onClose={onClose} />
      </DialogComponent>
    </Grid>
  );
};

export default OrderTemplateList;
